export const MDS_COUNTRY_CODE = {
  BAHRAIN: "BHR",
  INDIA: "IND",
  KUWAIT: "KWT",
  QATAR: "QAT",
  OMAN: "OMN",
  SAUDI_ARABIA: "SAU",
  UAE: "ARE",
  USA: "USA",
};

export const INDIA_COUNTRY_CODE = MDS_COUNTRY_CODE.INDIA;

export const COUNTRY_ID = {
  INDIA: 1,
  BAHRAIN: 2,
  KUWAIT: 3,
  QATAR: 4,
  OMAN: 5,
  SAUDI_ARABIA: 6,
  UAE: 7,
  USA: 8,
};

export const GCC_COUNTRY_IDS = [
  COUNTRY_ID.BAHRAIN,
  COUNTRY_ID.KUWAIT,
  COUNTRY_ID.QATAR,
  COUNTRY_ID.OMAN,
  COUNTRY_ID.SAUDI_ARABIA,
  COUNTRY_ID.UAE,
];

export const COUNTRY_CODE_BY_COUNTRY_ID = Object.fromEntries(
  Object.entries(COUNTRY_ID).map(([country, id]) => [id, MDS_COUNTRY_CODE[country]]),
);

export const getCountryCodeByCountryId = (countryId) => {
  if (countryId == null || countryId === "") {
    return null;
  }
  return COUNTRY_CODE_BY_COUNTRY_ID[Number(countryId)] || null;
};

export const ADDRESS_REGEX = {
  NAME: /^[A-Za-z.\s]+$/,
  ADDRESS_LINE: /^[A-Za-z0-9\-,"'#./()&:;\s]+$/,
  CITY: /^[A-Za-z\-,"'.\s]+$/,
  DISTRICT: /^[A-Za-z0-9\-,"'.\s]+$/,
  LANDMARK: /^[A-Za-z0-9\-,_./\s]+$/,
  US_STATE: /^[A-Z]{2}$/,
  US_ZIP: /^\d{5}(-\d{4})?$/,
  US_ZIP_EXTENDED: /^\d{5}-\d{4}$/,
  FIVE_DIGIT_ZIP: /^\d{5}$/,
  THREE_DIGIT_ZIP: /^\d{3}$/,
  SIX_DIGIT_ZIP: /^\d{6}$/,
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

export const ADDRESS_LENGTH_LIMITS = {
  NAME: { min: 3, max: 25 },
  ADDRESS_LINE_1: { min: 25, max: 100 },
  CITY: { min: 3, max: 50 },
  DISTRICT: { min: 2, max: 50 },
  LANDMARK: { min: 3, max: 50 },
};

export const ADDRESS_SCHEMA_LIMITS = {
  CITY_MAX: 30,
  ADDRESS_LINE_MIN: 25,
  ADDRESS_LINE_MAX: 100,
  NAME_MAX: 50,
};

export const ADDRESS_ERROR_MESSAGES = {
  NAME: "Use 3 – 25 characters",
  NAME_SPECIAL_CHARS: "Invalid special characters detected. You can use ( . )",
  NAME_SCHEMA_MAX: "Name cannot be more than 50 characters",
  ADDRESS_LINE_1: "Please enter a valid address. Use 25 – 100 characters",
  ADDRESS_LINE_SPECIAL_CHARS:
    'Invalid special characters detected. You can use ( - – " \' # , & () . : ; / )',
  CITY: "Please enter a valid city. Use 3 – 50 characters",
  CITY_SCHEMA_MAX: "City cannot be more than 30 characters",
  DISTRICT: "please enter a valid Area/District. Use 2 – 50 characters",
  DISTRICT_INVALID: "Please enter a valid Area/District.",
  DISTRICT_SPECIAL_CHARS:
    'Invalid special characters detected. You can use ( - – “ ‘ , . )',
  LANDMARK: "Use 3 – 50 characters",
  LANDMARK_SPECIAL_CHARS:
    "Invalid special characters detected. You can use ( - ,_.. / )",
  POSTAL_CODE_5_DIGIT: "please enter a valid 5 digit postal code",
  POSTAL_CODE_3_DIGIT: "please enter a valid 3 digit postal code",
  POSTAL_CODE_US_ZIP_EXTENDED:
    "please enter a valid 9 digit extended zip code",
  POSTAL_CODE_6_DIGIT: "please enter a valid 6 digit postal code",
  STATE_US: "Please enter a valid state. Example, for New-York NY, for Texas TX",
  STATE_REQUIRED: "State is required",
  PHONE: "Invalid Recipient Phone Number for the selected country",
  EMAIL: "Please enter a valid email address",
  SCA_PARENT_NAME:
    "For SCA orders, parent name must be provided in the billing address.",
  SCA_STATE_MISMATCH: "The Pincode is not valid for the tenant.",
  SHIPPING_REQUIRED:
    "Shipping address is required when the cart contains book/hardware components.",
  LEADING_TRAILING_SPACES: "Please remove leading/trailing spaces",
  INVALID_UTF8:
    "Invalid characters detected. Please use valid UTF-8 text.",
};

export const POSTAL_CODE_FORMATS = {
  INDIA_6_DIGIT: {
    regex: ADDRESS_REGEX.SIX_DIGIT_ZIP,
    message: ADDRESS_ERROR_MESSAGES.POSTAL_CODE_6_DIGIT,
    maxLength: 6,
  },
  US_ZIP_BILLING: {
    regex: ADDRESS_REGEX.FIVE_DIGIT_ZIP,
    message: ADDRESS_ERROR_MESSAGES.POSTAL_CODE_5_DIGIT,
    maxLength: 5,
  },
  US_ZIP_SHIPPING: {
    regex: ADDRESS_REGEX.US_ZIP_EXTENDED,
    message: ADDRESS_ERROR_MESSAGES.POSTAL_CODE_US_ZIP_EXTENDED,
    maxLength: 10,
  },
  FIVE_DIGIT: {
    regex: ADDRESS_REGEX.FIVE_DIGIT_ZIP,
    message: ADDRESS_ERROR_MESSAGES.POSTAL_CODE_5_DIGIT,
    maxLength: 5,
  },
  THREE_DIGIT: {
    regex: ADDRESS_REGEX.THREE_DIGIT_ZIP,
    message: ADDRESS_ERROR_MESSAGES.POSTAL_CODE_3_DIGIT,
    maxLength: 3,
  },
};

export const ADDRESS_FIELD_CONFIG_BY_COUNTRY_CODE = {
  [MDS_COUNTRY_CODE.INDIA]: {
    usesPostalLookup: true,
    showPostalCode: true,
    requirePostalCodeId: true,
    showDistrict: true,
    requireDistrictId: true,
    districtReadOnly: true,
    showState: true,
    requireStateId: true,
    stateReadOnly: true,
    postalCodeFormat: "INDIA_6_DIGIT",
  },
  [MDS_COUNTRY_CODE.USA]: {
    showPostalCode: true,
    requirePostalCode: true,
    postalCodeFormat: "US_ZIP",
    showState: true,
    requireStateName: true,
    isUsState: true,
  },
  [MDS_COUNTRY_CODE.UAE]: {
    showDistrict: true,
    requireDistrictName: true,
  },
  [MDS_COUNTRY_CODE.SAUDI_ARABIA]: {
    showDistrict: true,
    requireDistrictName: true,
    showPostalCode: true,
    requirePostalCode: true,
    postalCodeFormat: "FIVE_DIGIT",
  },
  [MDS_COUNTRY_CODE.QATAR]: {},
  [MDS_COUNTRY_CODE.OMAN]: {
    showPostalCode: true,
    requirePostalCode: true,
    postalCodeFormat: "THREE_DIGIT",
  },
  [MDS_COUNTRY_CODE.KUWAIT]: {
    showPostalCode: true,
    requirePostalCode: true,
    postalCodeFormat: "FIVE_DIGIT",
  },
  [MDS_COUNTRY_CODE.BAHRAIN]: {},
};

export const getAddressConfigByCountryCode = (countryCode) => {
  if (!countryCode) {
    return {};
  }
  const normalizedCode = String(countryCode).trim().toUpperCase();
  return ADDRESS_FIELD_CONFIG_BY_COUNTRY_CODE[normalizedCode] || {};
};

export const resolvePostalCodeFormat = (
  countryCode,
  addressType = "billing",
) => {
  const config = getAddressConfigByCountryCode(countryCode);
  const formatKey = config.postalCodeFormat;
  if (formatKey === "US_ZIP") {
    return addressType === "shipping" ? "US_ZIP_SHIPPING" : "US_ZIP_BILLING";
  }
  return formatKey;
};
